import {
  ContentfulCrossSelling,
  ContentfulCrossSellingSchema,
  ContentfulSearchPage,
  ContentfulSearchPageSchema,
  ContentfulEntriesRequestParamsSchema,
  CancellationPolicies,
  CancellationPoliciesSchema,
  ContentfulAviosOnlyPromotionContent,
  ContentfulAviosOnlyPromotionContentSchema,
  ContentfulTermsAndConditions,
  ContentfulStaticPageContent,
  ContentfulStaticAsset,
  ContentfulStaticContentSchema,
  ContentfulStaticContent,
  ContentfulStickyBannerType,
  ContentfulPromotionBannerType,
  ContentfulEducationalComponent,
  ContentfulEducationalComponentSchema,
} from '@reward-platform/contentful'
import { Iata } from '@reward-platform/platform-config'
import qs from 'qs'
import { createServiceSchemaValidator, createServiceSchemaValidatorAsync } from './utils'
import { PlatformError } from '../utils/errors'
import { nextApiClient } from './clients/nextApiClient'

/**
 * Get Search Page data
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @param opCo fetch the Search Page of a particular opCo
 * @param locale fetch the Search Page of a particular locale
 * @returns a searchPageCollection with object organised by AncillaryType
 */

export const getSearchPageContent = async (
  opCo: Iata,
  locale?: string,
  preview?: boolean
): Promise<ContentfulSearchPage> => {
  const validateParams = createServiceSchemaValidator(ContentfulEntriesRequestParamsSchema)
  const params = validateParams({
    opCo,
    ...(locale ? { locale } : { locale: 'en-GB' }),
    ...(preview ? { preview } : {}),
  })

  const { data } = await nextApiClient.get(`/contentful/search-page?${qs.stringify(params)}`)

  const validateContentfulData = createServiceSchemaValidatorAsync(ContentfulSearchPageSchema)
  return validateContentfulData(data)
}

interface MessageCluster {
  [key: string]: string | MessageCluster
}
/**
 * Request Message Cluster(s) for a specific locale from Contentful
 * @param {string[]} clusters Keys used to identify the clusters
 * @param {string} locale Locale to retrieve the messages in
 * @param {boolean} preview Whether to retrieve the unpublished results from Contentful
 * @returns {Promise<Clusters>} An asynchronous response containing the clusters with their messages
 */

export const getMessageClusters = async (
  opCo: Iata,
  clusters: string[],
  locale: string,
  preview?: boolean
): Promise<Record<string, MessageCluster>> => {
  if (!clusters?.length || !locale) {
    throw new PlatformError('Missing message clusters parameter')
  }

  const params = {
    opCo,
    clusters: clusters.toString(),
    locale,
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(`/contentful/cluster`, { params })

  return data
}

/**
 * Get Terms and Conditions content type data
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @param locale fetch the terms and conditions of a particular locale
 * @param identifier fetch based on a specific identifier
 * @returns Terms and Condition collection
 */

export const getTermsAndConditions = async (
  opCo: Iata,
  identifier: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulTermsAndConditions> => {
  if (!opCo) {
    throw new PlatformError('Missing T&Cs content parameter')
  }

  const params = {
    opCo,
    ...(identifier ? { identifier } : {}),
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(
    `/contentful/terms-and-conditions?${qs.stringify(params)}`
  )
  return data
}

/**
 * Get Disruption Message data
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @param locale fetch the Disruption Messages of a particular locale
 * @returns a DisruptionMessageCollection
 */

export const getPromotionBanners = async (
  opCo: Iata,
  locale?: string,
  preview?: boolean
): Promise<Array<ContentfulPromotionBannerType>> => {
  if (!opCo) {
    throw new PlatformError('Missing promotion banners content parameter')
  }

  const params = {
    opCo,
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(`/contentful/promotion-banner?${qs.stringify(params)}`)
  return data
}

export const getStickyBanners = async (
  opCo: Iata,
  locale?: string,
  preview?: boolean
): Promise<Array<ContentfulStickyBannerType>> => {
  if (!opCo) {
    throw new PlatformError('Missing sticky banners content parameter')
  }

  const params = {
    opCo,
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(`/contentful/sticky-banner?${qs.stringify(params)}`)
  return data
}

/**
 * Get Static Page content (e.g. T&Cs and Cookie Policy)
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @param locale fetch the Static Page of a particular locale
 * @returns a static page collection
 */

export const getStaticPageContent = async (
  opCo: Iata,
  identifier?: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulStaticPageContent> => {
  if (!opCo) {
    throw new PlatformError('Missing static page content parameter')
  }

  const params = {
    opCo,
    ...(identifier ? { identifier } : {}),
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(
    `/contentful/static-page-content?${qs.stringify(params)}`
  )
  return data
}

export const getStaticTextContent = async (
  opCo: Iata,
  identifier?: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulStaticContent> => {
  if (!opCo) {
    throw new PlatformError('Missing static content parameter')
  }

  const params = {
    opCo,
    ...(identifier ? { identifier } : {}),
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(`/contentful/static-content?${qs.stringify(params)}`)
  return data
}

/**
 * Get an Educational component (heading and icon + text items)
 * @param opCo fetch the entry tagged for a particular opCo
 * @param identifier the entry identifier
 * @param locale fetch the entry of a particular locale
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @returns the educational component, without items when no entry exists for the opCo
 */
export const getEducationalComponent = async (
  opCo: Iata,
  identifier: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulEducationalComponent> => {
  const params = {
    opCo,
    identifier,
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(
    `/contentful/educational-component?${qs.stringify(params)}`
  )

  const validateContentfulData = createServiceSchemaValidator(ContentfulEducationalComponentSchema)
  return validateContentfulData(data)
}

/**
 * Get Asset content
 * @param preview Whether to fetch the latest preview (unpublished) content
 * @param locale fetch the image of a particular locale
 * @returns a static single image
 */

export const getAssetContent = async (
  opCo: Iata,
  identifier: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulStaticAsset | undefined> => {
  const params = {
    opCo,
    ...(identifier ? { identifier } : {}),
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  try {
    const { data } = await nextApiClient.get(`/contentful/static-asset?${qs.stringify(params)}`)
    return data
  } catch {
    return undefined
  }
}

export const getCrossSelling = async (
  opCo: Iata,
  identifier?: string,
  locale?: string,
  preview?: boolean
): Promise<ContentfulCrossSelling> => {
  if (!opCo) {
    throw new PlatformError('Missing cross selling content parameter')
  }

  const params = {
    opCo,
    ...(identifier ? { identifier } : {}),
    ...(locale ? { locale } : { locale: 'en-US' }),
    ...(preview ? { preview } : {}),
  }

  const { data } = await nextApiClient.get(`/contentful/cross-selling?${qs.stringify(params)}`)

  const validateContentfulData = createServiceSchemaValidator(ContentfulCrossSellingSchema)
  return validateContentfulData(data)
}

export const getAviosOnlyPromotion = async (
  opCo: Iata,
  signal: AbortSignal,
  locale = 'en-GB',
  preview = false
): Promise<ContentfulAviosOnlyPromotionContent> => {
  const params = {
    opCo,
    locale,
    preview,
  }
  const { data } = await nextApiClient.get(
    `/contentful/avios-only-promotion?${qs.stringify(params)}`,
    { signal }
  )

  const validateContentfulData = createServiceSchemaValidatorAsync(
    ContentfulAviosOnlyPromotionContentSchema
  )
  return validateContentfulData(data)
}

export const getCancellationPolicies = async (
  locale?: string,
  preview?: boolean
): Promise<CancellationPolicies> => {
  const params = {
    ...(locale ? { locale } : { locale: 'en-GB' }),
    ...(preview ? { preview } : {}),
  }
  const { data } = await nextApiClient.get(
    `/contentful/cancellation-policies?${qs.stringify(params)}`
  )

  const validateContentfulData = createServiceSchemaValidator(CancellationPoliciesSchema)
  return validateContentfulData(data)
}

export const getPackageTravelLtaOverview = async (
  opCo: Iata,
  locale?: string,
  preview?: boolean
): Promise<ContentfulStaticContent | undefined> => {
  const params = {
    opCo,
    ...(locale ? { locale } : { locale: 'en-GB' }),
    ...(preview ? { preview } : {}),
  }

  const { data, status } = await nextApiClient.get(
    `/contentful/package-travel-lta-overview?${qs.stringify(params)}`,
    {
      validateStatus(statusCode) {
        return (statusCode > 199 && statusCode < 300) || statusCode === 404
      },
    }
  )

  if (status === 404) {
    return undefined
  }

  const validateContentfulData = createServiceSchemaValidatorAsync(ContentfulStaticContentSchema)
  return validateContentfulData(data)
}
